import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { Device, DeviceSIMAssignment } from '../../core/models/device.model';
import { DeviceSIMService } from '../../core/services/device-sim.service';
import { DeviceService } from '../../core/services/device.service';
import { OperationsService } from '../../core/services/operations.service';
import { OrganisationService } from '../../core/services/organisation.service';
import { UserService } from '../../core/services/user.service';
import { VehicleService } from '../../core/services/vehicle.service';
import { AuthService } from '../../core/services/auth.service';
import { LookupService } from '../../core/services/lookup.service';
import { StatusLookup, UserRoleLookup } from '../../core/models/lookup.model';
import { normalizeUserRole, UserRole } from '../../core/enums/user-role.enum';
import { ConfirmDialogComponent } from '../../shared/components/confirm-dialog/confirm-dialog.component';

interface WorkspaceField {
  key: string;
  label: string;
  type: 'text' | 'number' | 'date' | 'password' | 'select';
  required?: boolean;
  options?: Array<{ label: string; value: any }>;
  wide?: boolean;
}

interface WorkspaceConfig {
  icon: string;
  entityName: string;
  idKey: string;
  fields: WorkspaceField[];
  columns: Array<{ key: string; label: string }>;
  searchableKeys: string[];
}

interface NotificationRow {
  id?: string;
  title: string;
  type?: string;
  contactMethod?: string;
  contactValue?: string;
  status?: string;
  detail?: string;
  failureReason?: string;
  time?: string;
  sentAt?: string;
}

@Component({
  selector: 'app-operations-workspace',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, RouterModule, ConfirmDialogComponent],
  templateUrl: './operations-workspace.component.html',
  styleUrls: ['./operations-workspace.component.css']
})
export class OperationsWorkspaceComponent implements OnInit {
  title = 'Operations Workspace';
  description = 'Manage daily fleet tracking operations from this workspace.';
  apiTag = 'Operations';
  config = this.getConfig('Operations');

  rows: any[] = [];
  filteredRows: any[] = [];
  organisations: any[] = [];
  statuses: StatusLookup[] = [];
  userRoles: UserRoleLookup[] = [];
  vehicles: any[] = [];
  deviceSIMs: any[] = [];
  operationsCards: Array<{
    label: string;
    value: string;
    icon: string;
    detail: string;
    metrics: Array<{ label: string; value: string }>;
    items?: Array<{ title: string; status?: string; detail?: string; time?: string }>;
  }> = [];
  notificationRows: NotificationRow[] = [];
  notificationStatusFilter = '';
  notificationContactMethodFilter = '';
  notificationTake = 100;
  form!: FormGroup;
  selectedRow: any | null = null;
  rowPendingDelete: any | null = null;
  showForm = false;
  isLoading = false;
  isSaving = false;
  errorMessage = '';
  successMessage = '';
  searchTerm = '';
  pendingSIMSave: 'confirm-removal' | 'resolve-primary' | null = null;

  constructor(
    private route: ActivatedRoute,
    private fb: FormBuilder,
    private authService: AuthService,
    private userService: UserService,
    private vehicleService: VehicleService,
    private deviceService: DeviceService,
    private simService: DeviceSIMService,
    private organisationService: OrganisationService,
    private lookupService: LookupService,
    private operationsService: OperationsService
  ) {}

  ngOnInit(): void {
    this.route.data.subscribe(data => {
      this.title = data['title'] || 'Operations Workspace';
      this.description = data['description'] || 'Manage daily fleet tracking operations from this workspace.';
      this.apiTag = data['apiTag'] || 'Operations';
      this.config = this.getConfig(this.apiTag);
      this.buildForm();
      this.loadReferenceData();
      this.loadRows();
    });
  }

  get icon(): string {
    return this.config.icon;
  }

  get isOperationsView(): boolean {
    return this.apiTag === 'Operations';
  }

  get isReportsView(): boolean {
    return this.apiTag === 'Reports';
  }

  get isSystemLogsView(): boolean {
    return this.apiTag === 'SystemLogs';
  }

  get isNotificationAuditView(): boolean {
    return this.isReportsView || this.isSystemLogsView;
  }

  get isCrudView(): boolean {
    return ['User', 'Drivers', 'Profile', 'Device', 'DeviceSIM'].includes(this.apiTag);
  }

  get idKey(): string {
    return this.config.idKey;
  }

  loadReferenceData(): void {
    const needsOrganisations = ['User', 'Drivers', 'Profile'].includes(this.apiTag);
    const needsStatuses = ['User', 'Drivers', 'Profile', 'DeviceSIM'].includes(this.apiTag);
    const needsUserRoles = ['User', 'Drivers', 'Profile'].includes(this.apiTag);
    const needsVehicles = this.apiTag === 'Device';
    const needsDeviceSIMs = this.apiTag === 'Device';

    if (!needsOrganisations && !needsStatuses && !needsUserRoles && !needsVehicles && !needsDeviceSIMs) {
      return;
    }

    forkJoin({
      organisations: needsOrganisations
        ? this.organisationService.getOrganisations().pipe(catchError(error => this.referenceFallback('organisations', error)))
        : of([]),
      statuses: needsStatuses
        ? this.lookupService.getStatuses().pipe(catchError(error => this.referenceFallback('statuses', error)))
        : of([]),
      userRoles: needsUserRoles
        ? this.lookupService.getUserRoles().pipe(catchError(error => this.referenceFallback('user roles', error)))
        : of([]),
      vehicles: needsVehicles
        ? this.vehicleService.getVehicles(this.getOrganisationScope()).pipe(catchError(error => this.referenceFallback('vehicles', error)))
        : of([]),
      deviceSIMs: needsDeviceSIMs
        ? this.simService.getDeviceSIMs().pipe(catchError(error => this.referenceFallback('device SIMs', error)))
        : of([])
    }).subscribe(({ organisations, statuses, userRoles, vehicles, deviceSIMs }) => {
      this.organisations = organisations;
      this.statuses = statuses;
      this.userRoles = userRoles;
      this.vehicles = vehicles;
      this.deviceSIMs = deviceSIMs;
      this.updateReferenceOptions();
    });
  }

  loadRows(): void {
    this.isLoading = true;
    this.errorMessage = '';
    this.successMessage = '';

    if (this.apiTag === 'Operations') {
      this.loadOperationsHealth();
      return;
    }

    if (this.isNotificationAuditView) {
      this.loadReportsNotifications();
      return;
    }

    if (this.apiTag === 'Profile') {
      const currentUser = this.authService.getCurrentUser();
      this.rows = currentUser ? [currentUser] : [];
      this.applyFilters();
      this.isLoading = false;
      return;
    }

    const source$: Observable<any[]> = this.apiTag === 'Device'
      ? this.deviceService.getDevices()
      : this.apiTag === 'DeviceSIM'
        ? this.simService.getDeviceSIMs()
        : this.userService.getUsers(this.getOrganisationScope());

    source$.pipe(
      catchError(error => {
        console.error(`Unable to load ${this.config.entityName}:`, error);
        this.errorMessage = `Unable to load ${this.config.entityName.toLowerCase()} from the API.`;
        return of([]);
      })
    ).subscribe((rows: any[]) => {
      this.rows = this.apiTag === 'Drivers'
        ? rows.filter((row: any) => this.isDriver(row))
        : rows;
      this.applyFilters();
      this.updateReferenceOptions();
      this.isLoading = false;
    });
  }

  startCreate(): void {
    this.selectedRow = null;
    this.form.reset(this.defaultFormValue());
    this.showForm = true;
    this.successMessage = '';
    this.errorMessage = '';
  }

  editRow(row: any): void {
    this.selectedRow = row;
    this.form.patchValue(this.extractFormValue(row));
    this.updateReferenceOptions();
    this.showForm = true;
    this.successMessage = '';
    this.errorMessage = '';
  }

  saveRow(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    if (this.apiTag === 'Device' && !this.validateDeviceSIMSelection()) {
      return;
    }

    if (this.apiTag === 'Device' && this.selectedRow && this.requiresSIMRemovalConfirmation()) {
      const current = this.getDeviceSIMIds(this.selectedRow);
      const next = this.getFormSIMIds();
      this.pendingSIMSave = current.secondarySIMID && !next.primarySIMID
        ? 'resolve-primary'
        : 'confirm-removal';
      return;
    }

    this.performSave();
  }

  confirmSIMRemoval(): void {
    this.pendingSIMSave = null;
    this.performSave();
  }

  promoteSecondaryAndSave(): void {
    const current = this.getDeviceSIMIds(this.selectedRow);
    this.form.patchValue({ primarySIMID: current.secondarySIMID, secondarySIMID: null });
    this.pendingSIMSave = null;
    this.performSave();
  }

  removeBothAndSave(): void {
    this.form.patchValue({ primarySIMID: null, secondarySIMID: null });
    this.pendingSIMSave = null;
    this.performSave();
  }

  cancelSIMSave(): void {
    this.pendingSIMSave = null;
  }

  swapSIMs(): void {
    const { primarySIMID, secondarySIMID } = this.getFormSIMIds();
    if (primarySIMID && secondarySIMID) {
      this.form.patchValue({ primarySIMID: secondarySIMID, secondarySIMID: primarySIMID });
    }
  }

  onPrimarySIMChange(): void {
    if (!this.form.get('primarySIMID')?.value) {
      this.form.patchValue({ secondarySIMID: null });
    }
    this.errorMessage = '';
  }

  isFieldDisabled(field: WorkspaceField): boolean {
    return this.apiTag === 'Device' && field.key === 'secondarySIMID' && !this.form.get('primarySIMID')?.value;
  }

  isOptionDisabled(field: WorkspaceField, option: { value: any }): boolean {
    if (this.apiTag !== 'Device' || !option.value) return false;
    if (field.key === 'primarySIMID') return Number(option.value) === Number(this.form.get('secondarySIMID')?.value);
    if (field.key === 'secondarySIMID') return Number(option.value) === Number(this.form.get('primarySIMID')?.value);
    return false;
  }

  private performSave(): void {
    const payload = this.buildPayload();
    const id = this.selectedRow?.[this.idKey];
    this.isSaving = true;
    this.errorMessage = '';

    let request$: Observable<any> = this.apiTag === 'Device'
      ? id
        ? this.deviceService.updateDevice(id, payload).pipe(
            switchMap(() => this.haveSIMAssignmentsChanged()
              ? this.deviceService.updateSIMAssignments(id, this.getFormSIMIds())
              : of(null)),
            switchMap(() => this.deviceService.getDevice(id))
          )
        : this.deviceService.createDevice(payload)
      : this.apiTag === 'DeviceSIM'
        ? id
          ? this.simService.updateDeviceSIM(id, payload)
          : this.simService.createDeviceSIM(payload)
        : id
          ? this.userService.updateUser(id, payload)
          : this.userService.createUser(payload);

    request$.subscribe({
      next: () => {
        this.successMessage = `${this.config.entityName} saved.`;
        this.isSaving = false;
        this.showForm = false;
        this.selectedRow = null;
        this.loadRows();
      },
      error: (error: any) => {
        console.error(`Unable to save ${this.config.entityName}:`, error);
        this.errorMessage = this.getApiErrorMessage(error, `Unable to save ${this.config.entityName.toLowerCase()}.`);
        this.isSaving = false;
      }
    });
  }

  deleteRow(row: any): void {
    const id = row?.[this.idKey];
    if (!id) {
      return;
    }

    this.rowPendingDelete = row;
  }

  cancelDeleteRow(): void {
    this.rowPendingDelete = null;
  }

  confirmDeleteRow(): void {
    const row = this.rowPendingDelete;
    const id = row?.[this.idKey];
    if (!id) {
      return;
    }

    const request$ = this.apiTag === 'Device'
      ? this.deviceService.deleteDevice(id)
      : this.apiTag === 'DeviceSIM'
        ? this.simService.deleteDeviceSIM(id)
        : this.userService.deleteUser(id);

    request$.subscribe({
      next: () => {
        this.successMessage = `${this.config.entityName} deleted.`;
        this.rowPendingDelete = null;
        this.loadRows();
      },
      error: error => {
        console.error(`Unable to delete ${this.config.entityName}:`, error);
        const fallback = this.apiTag === 'DeviceSIM' && error?.status === 409
          ? 'This SIM is assigned to a device. Unassign it from the device before deleting it.'
          : `Unable to delete ${this.config.entityName.toLowerCase()}.`;
        const apiMessage = this.getApiErrorMessage(error, fallback);
        this.errorMessage = this.apiTag === 'DeviceSIM' && error?.status === 409
          ? apiMessage === fallback ? fallback : `${apiMessage} Unassign the SIM from its device before deleting it.`
          : apiMessage;
        this.rowPendingDelete = null;
      }
    });
  }

  cancelForm(): void {
    this.showForm = false;
    this.selectedRow = null;
    this.form.reset(this.defaultFormValue());
  }

  applyFilters(): void {
    const term = this.searchTerm.trim().toLowerCase();
    this.filteredRows = !term
      ? [...this.rows]
      : this.rows.filter(row => this.config.searchableKeys.some(key => String(this.resolveDisplayValue(row, key) || '').toLowerCase().includes(term)));
  }

  resolveValue(row: any, key: string): any {
    return key.split('.').reduce((value, part) => value?.[part], row);
  }

  resolveDisplayValue(row: any, key: string): any {
    const value = this.resolveValue(row, key);
    if (value !== null && value !== undefined && value !== '') {
      return value;
    }

    if (key === 'statusName') {
      return this.lookupStatusName(row?.statusID);
    }

    if (key === 'userRoleName') {
      return this.lookupUserRoleName(row?.userRoleID);
    }

    if (key === 'organisationName') {
      return row?.organisation?.orgName || row?.organisation?.organisationName;
    }

    if (key === 'vehicleRegistration') {
      return row?.vehicle?.vehicleRegistration;
    }

    if (key === 'vehicleLabel') {
      return row?.vehicle?.vehicleLabel;
    }

    if (key === 'deviceSIMNumber') {
      return row?.deviceSIM?.deviceSIMNumber || row?.deviceSIM?.simNumber;
    }

    if (key === 'deviceSIMProvider') {
      return row?.deviceSIM?.deviceSIMProvider || row?.deviceSIM?.simProvider;
    }

    if (key === 'deviceSerialNumber') {
      return row?.device?.deviceSerialNumber;
    }

    if (key === 'deviceSIMLink') {
      return row?.deviceID
        ? `${row.deviceSerialNumber || `Device ${row.deviceID}`} · ${row.deviceSIMSlot || 'Assigned'}`
        : 'Available';
    }

    if (key === 'inventoryState') {
      return this.getDeviceInventoryState(row);
    }

    if (key === 'simSummary') {
      return this.getDeviceSIMSummary(row);
    }

    return value;
  }

  getColumnValue(row: any, key: string): string {
    const value = this.resolveDisplayValue(row, key);

    if (key.toLowerCase().includes('date') && value) {
      return new Date(value).toLocaleDateString();
    }

    if (typeof value === 'boolean') {
      return value ? 'Active' : 'Inactive';
    }

    return value === null || value === undefined || value === '' ? '-' : String(value);
  }

  private buildForm(): void {
    const controls = this.config.fields.reduce((group, field) => {
      group[field.key] = ['', field.required ? Validators.required : []];
      return group;
    }, {} as Record<string, any>);

    this.form = this.fb.group(controls);
  }

  private loadOperationsHealth(): void {
    this.isLoading = true;

    forkJoin({
      health: this.operationsService.getHealth().pipe(catchError(error => this.operationFallback('health', error))),
      notifications: this.operationsService.getNotifications().pipe(catchError(error => this.operationFallback('notifications', error))),
      heartbeat: this.operationsService.getFleetHeartbeat().pipe(catchError(error => this.operationFallback('fleet heartbeat', error)))
    }).subscribe(({ health, notifications, heartbeat }) => {
      this.notificationRows = this.extractNotificationItems(notifications, 100);
      this.operationsCards = [
        {
          icon: 'favorite',
          label: 'API health',
          value: this.summarizeValue(health, 'status'),
          detail: this.describeOperationsData(health, 'Health endpoint returned successfully.'),
          metrics: this.extractMetrics(health)
        },
        {
          icon: 'notifications_active',
          label: 'Notifications',
          value: this.summarizeNotifications(notifications),
          detail: this.describeNotifications(notifications),
          metrics: this.extractNotificationMetrics(notifications)
        },
        {
          icon: 'monitor_heart',
          label: 'Fleet heartbeat',
          value: this.summarizeHeartbeat(heartbeat),
          detail: this.describeHeartbeat(heartbeat),
          metrics: this.extractHeartbeatMetrics(heartbeat),
          items: this.extractHeartbeatItems(heartbeat)
        }
      ];
      this.isLoading = false;
    });
  }

  private loadReportsNotifications(): void {
    this.isLoading = true;
    this.operationsCards = [];

    this.operationsService.getNotifications({
      status: this.notificationStatusFilter,
      contactMethod: this.notificationContactMethodFilter,
      take: this.notificationTake
    }).pipe(
      catchError(error => this.operationFallback('notifications', error))
    ).subscribe(notifications => {
      this.notificationRows = this.extractNotificationItems(notifications, 100);
      this.isLoading = false;
    });
  }

  private operationFallback(label: string, error: any) {
    console.error(`Unable to load operations ${label}:`, error);
    this.errorMessage = 'Some operations metrics could not be loaded from the API.';
    return of(null);
  }

  private referenceFallback(label: string, error: any) {
    console.error(`Unable to load ${label}:`, error);
    return of([]);
  }

  private summarizeValue(input: any, preferredKey: string): string {
    const value = this.unwrapOperationData(input);
    if (value === null || value === undefined) {
      return 'Unavailable';
    }

    if (Array.isArray(value)) {
      return `${value.length} records`;
    }

    if (typeof value !== 'object') {
      return String(value);
    }

    return String(
      value[preferredKey]
      || value.status
      || value.health
      || value.state
      || value.message
      || value.apiStatus
      || 'Online'
    );
  }

  private summarizeCollection(input: any): string {
    const value = this.unwrapOperationData(input);
    if (Array.isArray(value)) {
      return `${value.length} items`;
    }

    return value ? this.summarizeValue(value, 'status') : 'Unavailable';
  }

  private summarizeNotifications(input: any): string {
    const value = this.unwrapOperationData(input);
    const notifications = this.extractCollection(value, ['notifications', 'items', 'data', 'results']);
    const unread = this.pickNumber(value, ['unreadCount', 'unread', 'pendingCount', 'pending']);

    if (unread !== null) {
      return `${unread} unread`;
    }

    if (notifications.length) {
      return `${notifications.length} notifications`;
    }

    return this.summarizeCollection(value);
  }

  private summarizeHeartbeat(input: any): string {
    const value = this.unwrapOperationData(input);
    const vehicles = this.extractCollection(value, ['vehicles', 'fleet', 'heartbeats', 'items', 'data']);
    const online = this.pickNumber(value, ['online', 'onlineCount', 'activeVehicles', 'healthy']);
    const offline = this.pickNumber(value, ['offline', 'offlineCount', 'inactiveVehicles', 'staleVehicles']);

    if (online !== null || offline !== null) {
      return `${online ?? 0} online / ${offline ?? 0} attention`;
    }

    if (vehicles.length) {
      const onlineCount = vehicles.filter(item => this.isHeartbeatOnline(item)).length;
      return `${onlineCount} online / ${vehicles.length - onlineCount} attention`;
    }

    return this.summarizeValue(value, 'status');
  }

  private describeOperationsData(input: any, fallback: string): string {
    const value = this.unwrapOperationData(input);

    if (value === null || value === undefined) {
      return 'Endpoint did not return data.';
    }

    if (Array.isArray(value)) {
      return value.length
        ? `${value.length} operational item${value.length === 1 ? '' : 's'} returned.`
        : 'No operational items returned.';
    }

    if (typeof value !== 'object') {
      return String(value);
    }

    return value.message
      || value.description
      || value.summary
      || value.detail
      || fallback;
  }

  private describeNotifications(input: any): string {
    const value = this.unwrapOperationData(input);
    const latest = this.extractNotificationItems(value)[0];
    return latest?.detail || latest?.title || this.describeOperationsData(value, 'Notification feed is available.');
  }

  private describeHeartbeat(input: any): string {
    const value = this.unwrapOperationData(input);
    const lastUpdated = this.pickValue(value, ['lastUpdated', 'lastHeartbeat', 'timestamp', 'generatedAt']);
    if (lastUpdated) {
      return `Last heartbeat ${this.formatMaybeDate(lastUpdated)}.`;
    }

    const item = this.extractHeartbeatItems(value)[0];
    return item?.detail || this.describeOperationsData(value, 'Fleet heartbeat endpoint returned successfully.');
  }

  private extractMetrics(input: any): Array<{ label: string; value: string }> {
    const value = this.unwrapOperationData(input);

    if (!value) {
      return [];
    }

    if (Array.isArray(value)) {
      return value.slice(0, 4).map((item, index) => ({
        label: this.toTitleCase(item?.type || item?.name || item?.title || `Item ${index + 1}`),
        value: String(item?.status || item?.message || item?.value || item?.createdDate || 'Available')
      }));
    }

    if (typeof value !== 'object') {
      return [];
    }

    const hiddenKeys = new Set(['message', 'description', 'summary', 'detail']);

    return Object.entries(value)
      .filter(([key, metricValue]) => !hiddenKeys.has(key) && metricValue !== null && metricValue !== undefined && typeof metricValue !== 'object')
      .slice(0, 6)
      .map(([key, metricValue]) => ({
        label: this.toTitleCase(key),
        value: String(metricValue)
      }));
  }

  private extractNotificationMetrics(input: any): Array<{ label: string; value: string }> {
    const value = this.unwrapOperationData(input);
    const notifications = this.extractCollection(value, ['notifications', 'items', 'data', 'results']);
    const severityCounts = notifications.reduce((counts, notification) => {
      const severity = String(this.pickValue(notification, ['severity', 'priority', 'level', 'type']) || 'Info');
      counts[severity] = (counts[severity] || 0) + 1;
      return counts;
    }, {} as Record<string, number>);

    const explicitMetrics = [
      this.metricFromNumber(value, ['total', 'totalCount', 'count'], 'Total'),
      this.metricFromNumber(value, ['unreadCount', 'unread', 'pendingCount'], 'Unread'),
      this.metricFromNumber(value, ['criticalCount', 'critical'], 'Critical')
    ].filter(Boolean) as Array<{ label: string; value: string }>;

    const severityMetrics = Object.entries(severityCounts)
      .slice(0, 4)
      .map(([label, count]) => ({ label: this.toTitleCase(label), value: String(count) }));

    return [...explicitMetrics, ...severityMetrics].slice(0, 6);
  }

  private extractHeartbeatMetrics(input: any): Array<{ label: string; value: string }> {
    const value = this.unwrapOperationData(input);
    const vehicles = this.extractCollection(value, ['vehicles', 'fleet', 'heartbeats', 'items', 'data']);
    const explicitMetrics = [
      this.metricFromNumber(value, ['totalVehicles', 'total', 'count'], 'Vehicles'),
      this.metricFromNumber(value, ['online', 'onlineCount', 'activeVehicles', 'healthy'], 'Online'),
      this.metricFromNumber(value, ['offline', 'offlineCount', 'inactiveVehicles'], 'Offline'),
      this.metricFromNumber(value, ['staleVehicles', 'staleCount', 'stale'], 'Stale')
    ].filter(Boolean) as Array<{ label: string; value: string }>;

    if (explicitMetrics.length) {
      return explicitMetrics;
    }

    if (!vehicles.length) {
      return this.extractMetrics(value);
    }

    const online = vehicles.filter(item => this.isHeartbeatOnline(item)).length;
    return [
      { label: 'Vehicles', value: String(vehicles.length) },
      { label: 'Online', value: String(online) },
      { label: 'Attention', value: String(vehicles.length - online) }
    ];
  }

  private extractNotificationItems(input: any, limit = 5): NotificationRow[] {
    return this.extractCollection(this.unwrapOperationData(input), ['notifications', 'items', 'data', 'results'])
      .slice(0, limit)
      .map(notification => {
        const bookingReference = this.pickValue(notification, ['bookingReference', 'BookingReference', 'reference']);
        const deliveryId = this.pickValue(notification, ['notificationDeliveryID', 'notificationDeliveryId', 'deliveryID', 'deliveryId', 'operatorNotificationDeliveryID']);
        const detail = String(this.pickValue(notification, ['message', 'description', 'body', 'detail', 'summary']) || '');
        return {
          id: deliveryId ? String(deliveryId) : undefined,
          title: String(this.pickValue(notification, ['title', 'subject']) || (bookingReference ? 'Booking notification' : 'Notification')),
          type: String(this.pickValue(notification, ['notificationType', 'type']) || ''),
          contactMethod: String(this.pickValue(notification, ['contactMethod', 'channel', 'method']) || ''),
          contactValue: String(this.pickValue(notification, ['contactValue', 'recipient', 'destination', 'email', 'phone']) || ''),
          status: String(this.pickValue(notification, ['severity', 'priority', 'level', 'status']) || ''),
          detail: [detail, bookingReference ? `Booking ${bookingReference}` : ''].filter(Boolean).join(' / '),
          failureReason: String(this.pickValue(notification, ['failureReason', 'FailureReason', 'failure_reason', 'errorReason', 'errorMessage']) || ''),
          time: this.formatMaybeDate(this.pickValue(notification, ['createdDate', 'createdAt', 'timestamp'])),
          sentAt: this.formatMaybeDate(this.pickValue(notification, ['sentAt', 'SentAt']))
        };
      });
  }

  private extractHeartbeatItems(input: any): Array<{ title: string; status?: string; detail?: string; time?: string }> {
    return this.extractCollection(this.unwrapOperationData(input), ['vehicles', 'fleet', 'heartbeats', 'items', 'data'])
      .slice(0, 5)
      .map(item => {
        const vehicle = this.pickValue(item, ['vehicleRegistration', 'registration', 'vehicleLabel', 'label', 'name']) || 'Vehicle';
        const status = this.pickValue(item, ['status', 'state', 'heartbeatStatus']) || (this.isHeartbeatOnline(item) ? 'Online' : 'Attention');
        const location = this.pickValue(item, ['location', 'lastLocation', 'address']);
        const speed = this.pickValue(item, ['speed', 'currentSpeed']);
        return {
          title: String(vehicle),
          status: String(status),
          detail: [location, speed !== undefined && speed !== null ? `${speed} km/h` : ''].filter(Boolean).join(' - '),
          time: this.formatMaybeDate(this.pickValue(item, ['lastSeen', 'lastHeartbeat', 'timestamp', 'updatedAt']))
        };
      });
  }

  private unwrapOperationData(value: any): any {
    if (value?.$values) {
      return value.$values;
    }

    if (value?.data !== undefined) {
      return this.unwrapOperationData(value.data);
    }

    if (value?.result !== undefined) {
      return this.unwrapOperationData(value.result);
    }

    if (value?.items !== undefined) {
      return this.unwrapOperationData(value.items);
    }

    return value;
  }

  private extractCollection(value: any, keys: string[]): any[] {
    const unwrapped = this.unwrapOperationData(value);
    if (Array.isArray(unwrapped)) {
      return unwrapped;
    }

    if (!unwrapped || typeof unwrapped !== 'object') {
      return [];
    }

    for (const key of keys) {
      const candidate = this.unwrapOperationData(unwrapped[key]);
      if (Array.isArray(candidate)) {
        return candidate;
      }
    }

    return [];
  }

  private pickValue(source: any, keys: string[]): any {
    if (!source || typeof source !== 'object') {
      return undefined;
    }

    for (const key of keys) {
      const value = source[key];
      if (value !== null && value !== undefined && value !== '') {
        return value;
      }
    }

    return undefined;
  }

  private pickNumber(source: any, keys: string[]): number | null {
    const value = this.pickValue(source, keys);
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : null;
  }

  private metricFromNumber(source: any, keys: string[], label: string): { label: string; value: string } | null {
    const value = this.pickNumber(source, keys);
    return value === null ? null : { label, value: String(value) };
  }

  private isHeartbeatOnline(item: any): boolean {
    const status = String(this.pickValue(item, ['status', 'state', 'heartbeatStatus']) || '').toLowerCase();
    if (status) {
      return ['online', 'active', 'healthy', 'ok', 'connected'].includes(status);
    }

    const online = this.pickValue(item, ['online', 'isOnline', 'isActive', 'healthy', 'connected']);
    return online === true || String(online).toLowerCase() === 'true';
  }

  private formatMaybeDate(value: any): string {
    if (!value) {
      return '';
    }

    const date = new Date(value);
    return Number.isNaN(date.getTime())
      ? String(value)
      : date.toLocaleString();
  }

  private toTitleCase(value: string): string {
    return String(value)
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      .replace(/[_-]+/g, ' ')
      .replace(/\b\w/g, character => character.toUpperCase());
  }

  private lookupStatusName(statusID: any): string {
    const numericStatusID = Number(statusID);
    return this.statuses.find(status => status.statusID === numericStatusID)?.statusName || '';
  }

  private lookupUserRoleName(userRoleID: any): string {
    const numericUserRoleID = Number(userRoleID);
    return this.userRoles.find(role => role.userRoleID === numericUserRoleID)?.userRoleName || '';
  }

  private getOrganisationScope(): number | undefined {
    const currentUser = this.authService.getCurrentUser();
    const role = normalizeUserRole(currentUser?.userRole);
    return role === UserRole.OPERATOR && currentUser?.organisationID
      ? currentUser.organisationID
      : undefined;
  }

  private isDriver(row: any): boolean {
    const role = String(row?.userRoleName || row?.userRole?.userRoleName || row?.userRole || '').toLowerCase();
    return role.includes('driver') || role.includes('operator');
  }

  private updateReferenceOptions(): void {
    const organisationField = this.config.fields.find(item => item.key === 'organisationID');
    if (organisationField) {
      organisationField.options = [
        { label: 'No organisation', value: null },
        ...this.organisations.map(organisation => ({
          label: organisation.orgName || organisation.organisationName || `Organisation ${organisation.organisationID}`,
          value: organisation.organisationID
        }))
      ];
    }

    const statusField = this.config.fields.find(item => item.key === 'statusID');
    if (statusField) {
      statusField.options = this.statuses.map(status => ({
        label: status.statusName || `Status ${status.statusID}`,
        value: status.statusID
      }));
    }

    const userRoleField = this.config.fields.find(item => item.key === 'userRoleID');
    if (userRoleField) {
      userRoleField.options = this.userRoles.map(role => ({
        label: role.userRoleName || `Role ${role.userRoleID}`,
        value: role.userRoleID
      }));
    }

    const vehicleField = this.config.fields.find(item => item.key === 'vehicleID');
    if (vehicleField) {
      const currentVehicleID = Number(this.selectedRow?.vehicleID) || null;
      const assignedVehicleIDs = new Set(this.rows
        .filter(device => Number(device.deviceID) !== Number(this.selectedRow?.deviceID))
        .map(device => Number(device.vehicleID))
        .filter(Boolean));
      vehicleField.options = [
        { label: 'Assign vehicle later', value: null },
        ...this.vehicles.filter(vehicle => Number(vehicle.vehicleID) === currentVehicleID || !assignedVehicleIDs.has(Number(vehicle.vehicleID))).map(vehicle => ({
          label: this.getVehicleOptionLabel(vehicle),
          value: vehicle.vehicleID
        }))
      ];
    }

    const currentSIMIds = this.getDeviceSIMIds(this.selectedRow);
    for (const key of ['primarySIMID', 'secondarySIMID']) {
      const field = this.config.fields.find(item => item.key === key);
      if (!field) continue;
      field.options = [
        { label: 'Assign SIM later', value: null },
        ...this.deviceSIMs
          .filter(sim => !sim.deviceID || Number(sim.deviceID) === Number(this.selectedRow?.deviceID)
            || Number(sim.deviceSIMID) === currentSIMIds.primarySIMID || Number(sim.deviceSIMID) === currentSIMIds.secondarySIMID)
          .map(sim => ({ label: this.getDeviceSIMOptionLabel(sim), value: sim.deviceSIMID }))
      ];
    }
  }

  private getVehicleOptionLabel(vehicle: any): string {
    return [
      vehicle.vehicleRegistration,
      vehicle.vehicleLabel,
      vehicle.organisationName || vehicle.organisation?.orgName
    ].filter(Boolean).join(' - ') || `Vehicle ${vehicle.vehicleID}`;
  }

  private getDeviceSIMOptionLabel(sim: any): string {
    return [
      sim.deviceSIMNumber || sim.simNumber,
      sim.deviceSIMProvider || sim.simProvider,
      sim.deviceSerialNumber ? `linked to ${sim.deviceSerialNumber}` : ''
    ].filter(Boolean).join(' - ') || `SIM ${sim.deviceSIMID}`;
  }

  private defaultFormValue(): Record<string, any> {
    const defaults: Record<string, any> = {};
    this.config.fields.forEach(field => {
      defaults[field.key] = field.type === 'number' ? null : '';
    });
    return defaults;
  }

  private extractFormValue(row: any): Record<string, any> {
    const values: Record<string, any> = {};
    this.config.fields.forEach(field => {
      const simIds = this.getDeviceSIMIds(row);
      const value = field.key === 'primarySIMID' ? simIds.primarySIMID
        : field.key === 'secondarySIMID' ? simIds.secondarySIMID
        : this.resolveValue(row, field.key);
      values[field.key] = field.type === 'date' && value
        ? String(value).slice(0, 10)
        : value ?? '';
    });
    return values;
  }

  private buildPayload(): Record<string, any> {
    const value = this.form.value;
    const selected = this.selectedRow || {};
    const now = new Date().toISOString();

    if (this.apiTag === 'Device') {
      const payload: Record<string, any> = {
        deviceID: selected.deviceID || 0,
        deviceModel: value.deviceModel || selected.deviceModel || '',
        deviceType: value.deviceType || selected.deviceType || null,
        deviceSerialNumber: value.deviceSerialNumber || selected.deviceSerialNumber || '',
        deviceImei: value.deviceImei || selected.deviceImei || selected.deviceIMEI || null,
        deviceInstallDate: this.toApiDate(value.deviceInstallDate || selected.deviceInstallDate || selected.deviceInstalledDate || now),
        deviceCreateDate: selected.deviceCreateDate || now,
        deviceModifyDate: now,
        vehicleID: this.nullableNumber(value.vehicleID)
      };
      if (!selected.deviceID) {
        payload['primarySIMID'] = this.nullableNumber(value.primarySIMID);
        payload['secondarySIMID'] = this.nullableNumber(value.secondarySIMID);
      }
      return payload;
    }

    if (this.apiTag === 'DeviceSIM') {
      return {
        deviceSIMID: selected.deviceSIMID || 0,
        deviceSIMPUKNumber: value.deviceSIMPUKNumber || selected.deviceSIMPUKNumber || null,
        deviceSIMNumber: value.deviceSIMNumber || selected.deviceSIMNumber || selected.simNumber || '',
        deviceSIMProvider: value.deviceSIMProvider || selected.deviceSIMProvider || selected.simProvider || '',
        deviceSIMContractType: value.deviceSIMContractType || selected.deviceSIMContractType || null,
        deviceSIMContractStartDate: this.toApiDate(value.deviceSIMContractStartDate || selected.deviceSIMContractStartDate || selected.simActivationDate || now),
        deviceSIMContractEndDate: this.toApiDate(value.deviceSIMContractEndDate || selected.deviceSIMContractEndDate || selected.simExpiryDate || now),
        deviceSIMModifyDate: now,
        statusID: this.toNumber(value.statusID, selected.statusID, 1)
      };
    }

    return {
      userID: selected.userID || 0,
      username: value.username || selected.username || selected.userName || '',
      userPassword: value.userPassword || selected.userPassword || selected.userPasswordHash || null,
      userPrefix: selected.userPrefix || null,
      userFirstName: value.userFirstName || selected.userFirstName || null,
      userLastName: value.userLastName || selected.userLastName || null,
      userCreateDate: selected.userCreateDate || selected.userCreatedDate || now,
      userModifyDate: now,
      organisationID: value.organisationID === '' ? null : value.organisationID ?? selected.organisationID ?? null,
      addressID: selected.addressID || null,
      contactID: selected.contactID || null,
      statusID: this.toNumber(value.statusID, selected.statusID, 1),
      userRoleID: this.toNumber(value.userRoleID, selected.userRoleID, 3)
    };
  }

  private toApiDate(value: string): string {
    return value && value.length === 10
      ? new Date(`${value}T00:00:00`).toISOString()
      : value;
  }

  private toNumber(value: any, fallback: any, defaultValue: number): number {
    const candidate = value ?? fallback ?? defaultValue;
    const numeric = Number(candidate);
    return Number.isFinite(numeric) ? numeric : defaultValue;
  }

  private nullableNumber(value: any): number | null {
    if (value === null || value === undefined || value === '') return null;
    const numeric = Number(value);
    return Number.isFinite(numeric) && numeric > 0 ? numeric : null;
  }

  private getDeviceSIMIds(device: any): { primarySIMID: number | null; secondarySIMID: number | null } {
    const assignments: DeviceSIMAssignment[] = Array.isArray(device?.simAssignments)
      ? device.simAssignments
      : device?.simAssignments?.$values || [];
    const assigned = (slot: string) => assignments.find(item => String(item.slot).toLowerCase() === slot.toLowerCase())?.deviceSIMID;
    return {
      primarySIMID: this.nullableNumber(device?.primarySIMID ?? assigned('Primary') ?? device?.deviceSIMID),
      secondarySIMID: this.nullableNumber(device?.secondarySIMID ?? assigned('Secondary'))
    };
  }

  private getFormSIMIds(): { primarySIMID: number | null; secondarySIMID: number | null } {
    return {
      primarySIMID: this.nullableNumber(this.form.get('primarySIMID')?.value),
      secondarySIMID: this.nullableNumber(this.form.get('secondarySIMID')?.value)
    };
  }

  private haveSIMAssignmentsChanged(): boolean {
    const current = this.getDeviceSIMIds(this.selectedRow);
    const next = this.getFormSIMIds();
    return current.primarySIMID !== next.primarySIMID || current.secondarySIMID !== next.secondarySIMID;
  }

  private requiresSIMRemovalConfirmation(): boolean {
    if (!this.haveSIMAssignmentsChanged()) return false;
    const current = this.getDeviceSIMIds(this.selectedRow);
    const next = this.getFormSIMIds();
    return Boolean((current.primarySIMID && ![next.primarySIMID, next.secondarySIMID].includes(current.primarySIMID))
      || (current.secondarySIMID && ![next.primarySIMID, next.secondarySIMID].includes(current.secondarySIMID)));
  }

  private validateDeviceSIMSelection(): boolean {
    const { primarySIMID, secondarySIMID } = this.getFormSIMIds();
    if (secondarySIMID && !primarySIMID) {
      this.errorMessage = 'Select a primary SIM before assigning a secondary SIM.';
      return false;
    }
    if (primarySIMID && primarySIMID === secondarySIMID) {
      this.errorMessage = 'Primary and secondary slots cannot use the same SIM.';
      return false;
    }
    return true;
  }

  private getDeviceInventoryState(device: Device): string {
    const sims = this.getDeviceSIMIds(device);
    if (!device.vehicleID) return 'Awaiting vehicle';
    if (!sims.primarySIMID) return 'Awaiting SIM';
    return sims.secondarySIMID ? 'Dual SIM ready' : 'Primary only';
  }

  private getDeviceSIMSummary(device: Device): string {
    const assignments: DeviceSIMAssignment[] = Array.isArray(device.simAssignments)
      ? device.simAssignments : (device.simAssignments as any)?.$values || [];
    const ids = this.getDeviceSIMIds(device);
    const label = (slot: 'Primary' | 'Secondary') => {
      const match = assignments.find(item => item.slot === slot);
      if (match) return `${slot}: ${match.deviceSIMNumber || `SIM ${match.deviceSIMID}`}${match.deviceSIMProvider ? ` · ${match.deviceSIMProvider}` : ''}`;
      const id = slot === 'Primary' ? ids.primarySIMID : ids.secondarySIMID;
      if (id) return `${slot}: ${slot === 'Primary' && device.deviceSIMNumber ? device.deviceSIMNumber : `SIM ${id}`}${slot === 'Primary' && device.deviceSIMProvider ? ` · ${device.deviceSIMProvider}` : ''}`;
      return `${slot}: Unassigned`;
    };
    return `${label('Primary')}\n${label('Secondary')}`;
  }

  private getApiErrorMessage(error: any, fallback: string): string {
    const body = error?.error;
    return body?.message || body?.detail || body?.title || (typeof body === 'string' && body.trim()) || fallback;
  }

  private getConfig(apiTag: string): WorkspaceConfig {
    const commonUserFields: WorkspaceField[] = [
      { key: 'username', label: 'Username', type: 'text', required: true },
      { key: 'userPassword', label: 'Password', type: 'password' },
      { key: 'userFirstName', label: 'First name', type: 'text' },
      { key: 'userLastName', label: 'Last name', type: 'text' },
      { key: 'organisationID', label: 'Organisation', type: 'select', options: [] },
      { key: 'userRoleID', label: 'Role', type: 'select', options: [], required: true },
      { key: 'statusID', label: 'Status', type: 'select', options: [], required: true }
    ];

    const configs: Record<string, WorkspaceConfig> = {
      User: {
        icon: 'people',
        entityName: 'User',
        idKey: 'userID',
        fields: commonUserFields,
        columns: [
          { key: 'username', label: 'Username' },
          { key: 'userFirstName', label: 'First name' },
          { key: 'userLastName', label: 'Last name' },
          { key: 'userRoleName', label: 'Role' },
          { key: 'organisationName', label: 'Organisation' },
          { key: 'statusName', label: 'Status' }
        ],
        searchableKeys: ['username', 'userFirstName', 'userLastName', 'userRoleName', 'organisationName', 'statusName']
      },
      Drivers: {
        icon: 'badge',
        entityName: 'Driver',
        idKey: 'userID',
        fields: commonUserFields,
        columns: [
          { key: 'username', label: 'Username' },
          { key: 'userFirstName', label: 'First name' },
          { key: 'userLastName', label: 'Last name' },
          { key: 'organisationName', label: 'Organisation' },
          { key: 'statusName', label: 'Status' }
        ],
        searchableKeys: ['username', 'userFirstName', 'userLastName', 'organisationName', 'statusName']
      },
      Profile: {
        icon: 'person',
        entityName: 'Profile',
        idKey: 'userID',
        fields: commonUserFields,
        columns: [
          { key: 'username', label: 'Username' },
          { key: 'userFirstName', label: 'First name' },
          { key: 'userLastName', label: 'Last name' },
          { key: 'organisationName', label: 'Organisation' },
          { key: 'userRoleName', label: 'Role' },
          { key: 'statusName', label: 'Status' }
        ],
        searchableKeys: ['username', 'userFirstName', 'userLastName', 'organisationName', 'userRoleName', 'statusName']
      },
      Device: {
        icon: 'devices',
        entityName: 'Device',
        idKey: 'deviceID',
        fields: [
          { key: 'deviceModel', label: 'Model', type: 'text', required: true },
          { key: 'deviceType', label: 'Type', type: 'text' },
          { key: 'deviceSerialNumber', label: 'Serial number', type: 'text', required: true },
          { key: 'deviceImei', label: 'IMEI', type: 'text' },
          { key: 'deviceInstallDate', label: 'Install date', type: 'date' },
          { key: 'vehicleID', label: 'Vehicle', type: 'select', options: [] },
          { key: 'primarySIMID', label: 'Primary SIM', type: 'select', options: [] },
          { key: 'secondarySIMID', label: 'Secondary SIM / failover', type: 'select', options: [] }
        ],
        columns: [
          { key: 'deviceSerialNumber', label: 'Serial' },
          { key: 'deviceModel', label: 'Model' },
          { key: 'deviceType', label: 'Type' },
          { key: 'deviceImei', label: 'IMEI' },
          { key: 'vehicleRegistration', label: 'Vehicle' },
          { key: 'organisationName', label: 'Organisation' },
          { key: 'simSummary', label: 'SIM assignments' },
          { key: 'inventoryState', label: 'Inventory state' }
        ],
        searchableKeys: ['deviceSerialNumber', 'deviceModel', 'deviceType', 'deviceImei', 'vehicleRegistration', 'vehicleLabel', 'organisationName', 'deviceSIMNumber', 'deviceSIMProvider']
      },
      DeviceSIM: {
        icon: 'sim_card',
        entityName: 'SIM',
        idKey: 'deviceSIMID',
        fields: [
          { key: 'deviceSIMNumber', label: 'SIM number', type: 'text', required: true },
          { key: 'deviceSIMPUKNumber', label: 'PUK number', type: 'text' },
          { key: 'deviceSIMProvider', label: 'Provider', type: 'text', required: true },
          { key: 'deviceSIMContractType', label: 'Contract type', type: 'text' },
          { key: 'deviceSIMContractStartDate', label: 'Start date', type: 'date' },
          { key: 'deviceSIMContractEndDate', label: 'End date', type: 'date' },
          { key: 'statusID', label: 'Status', type: 'select', options: [] }
        ],
        columns: [
          { key: 'deviceSIMNumber', label: 'SIM number' },
          { key: 'deviceSIMProvider', label: 'Provider' },
          { key: 'deviceSIMContractType', label: 'Contract' },
          { key: 'deviceSIMContractStartDate', label: 'Start' },
          { key: 'deviceSIMContractEndDate', label: 'End' },
          { key: 'deviceSIMLink', label: 'Assignment' },
          { key: 'statusName', label: 'Status' }
        ],
        searchableKeys: ['deviceSIMNumber', 'deviceSIMProvider', 'deviceSIMContractType', 'deviceSerialNumber', 'statusName']
      },
      Operations: {
        icon: 'monitor_heart',
        entityName: 'Operations',
        idKey: 'id',
        fields: [],
        columns: [],
        searchableKeys: []
      },
      Reports: {
        icon: 'assessment',
        entityName: 'Reports',
        idKey: 'id',
        fields: [],
        columns: [],
        searchableKeys: []
      },
      SystemLogs: {
        icon: 'manage_search',
        entityName: 'System logs',
        idKey: 'notificationDeliveryID',
        fields: [],
        columns: [],
        searchableKeys: []
      }
    };

    return configs[apiTag] || configs['Operations'];
  }
}
